import { generateKeyBetween, generateNKeysBetween } from "fractional-indexing";

// Fractional display-order keys for policy-field rows (limits, deductibles,
// sublimits). Keys sort lexicographically; a move rewrites a single key (plus a
// one-time seed of the group when keys are still absent), never the whole group.
// See the backend `sort_key` field and the update_report sort-only path.

/** A row's new sort_key to persist, paired with the row it belongs to. */
export interface SortKeyChange<T> {
  item: T;
  sortKey: string;
}

/**
 * Stable-sort a group by sort_key: keyed rows first in lexicographic order,
 * unkeyed (legacy) rows last in their original relative order.
 */
export function sortBySortKey<T>(
  items: T[],
  getKey: (item: T) => string | null | undefined
): T[] {
  return items
    .map((item, index) => ({ item, index, key: getKey(item) ?? null }))
    .sort((a, b) => {
      if (a.key === null && b.key === null) return a.index - b.index;
      if (a.key === null) return 1;
      if (b.key === null) return -1;
      if (a.key === b.key) return a.index - b.index;
      return a.key < b.key ? -1 : 1;
    })
    .map((entry) => entry.item);
}

/**
 * Compute the sort_key changes for a drag-and-drop reorder.
 *
 * `dropped` is the full row list in the order produced by the drop (PrimeReact's
 * onRowReorder `value`), and `draggedIndex` is where the dragged row landed in
 * it. Reordering is scoped to the dragged row's group (`sameGroup`), which keeps
 * a row from leaving its peril/classification — a cross-group drop just settles
 * the row at the nearest edge of its own group.
 *
 * Normally only the dragged row's key changes (one `keyBetween` of its new
 * neighbors). If the group has no keys yet, the whole group is seeded in its new
 * order — a one-time cost, after which every drag rewrites a single key.
 */
export function keysForDrop<T>(
  dropped: T[],
  draggedIndex: number,
  sameGroup: (a: T, b: T) => boolean,
  getKey: (item: T) => string | null | undefined
): SortKeyChange<T>[] {
  const dragged = dropped[draggedIndex];
  if (dragged === undefined) return [];

  // The dragged row's group, in the new (dropped) relative order.
  const group = dropped.filter((row) => sameGroup(row, dragged));
  const keys = group.map((row) => getKey(row) ?? null);

  if (keys.some((key) => key === null)) {
    const seeded = generateNKeysBetween(null, null, group.length);
    return group.flatMap((item, i) => {
      const sortKey = seeded[i];
      return sortKey === undefined ? [] : [{ item, sortKey }];
    });
  }

  const gi = group.indexOf(dragged);
  const keyAt = (i: number): string | null =>
    i >= 0 && i < keys.length ? (keys[i] ?? null) : null;
  return [
    {
      item: dragged,
      sortKey: generateKeyBetween(keyAt(gi - 1), keyAt(gi + 1)),
    },
  ];
}
